import { ImgID } from "../../image_loader/img_id.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { UIImage } from "./ui_image.js";
import { UIElementHolder } from "./ui_element_holder.js";
import { ColorScheme } from "./color_scheme.js";
import { AvatarUtils } from "../../common/avatar_utils.js";
import { loadFacebookAvatar } from "../../common/facebook_avatar_loader.js";
import { RoundedRectangleFill } from "./rounded_rectangle.js";

export class UIAvatarSelectionItem extends UIElementHolder
{
  public avatar_id: number;
  private is_selected: boolean = false;
  private on_click: () => void;
  private avatar_image!: UIImage;
  private border_size_offset: number;
  private frame_size_offset: number;
  private registration_icon_id?: ImgID;
  private border_color: RoundedRectangleFill;
  private facebook_av_url?: string | null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    avatar_id: number,
    on_click: () => void,
    border_size_offset: number,
    frame_size_offset: number,
    registration_icon_id?: ImgID,
    is_scalable_on_click = false,
    border_color?: RoundedRectangleFill,
    facebook_av_url?: string | null
  )
  {
    super( ctx, x, y, width, height, false, is_scalable_on_click );
    this.avatar_id = avatar_id;
    this.on_click = on_click;
    this.border_size_offset = border_size_offset;
    this.frame_size_offset = frame_size_offset;
    this.registration_icon_id = registration_icon_id;
    this.border_color = border_color ?? ColorScheme.avatar_stroke;
    this.facebook_av_url = facebook_av_url;

    this.createAvatarImage();
    this.setOnClick( () =>
    {
      this.on_click();
    } );
  }

  public setAvatarId( avatar_id: number, facebook_av_url?: string | null )
  {
    this.avatar_id = avatar_id;
    if ( facebook_av_url !== undefined )
      this.facebook_av_url = facebook_av_url;

    this.avatar_image.setImageId( AvatarUtils.getAvatarImgId( avatar_id ) );
    this.applyFacebookPhotoIfNeeded();
    this.forceRedraw();
  }

  private createAvatarImage()
  {
    const avatar_size = this.rect.getW() - this.border_size_offset * 2;
    const avatar_x = this.border_size_offset;
    const avatar_y = this.border_size_offset;

    const avatar_img_id = AvatarUtils.getAvatarImgId( this.avatar_id );

    this.avatar_image = new UIImage(
      this.ctx,
      avatar_x,
      avatar_y,
      avatar_size,
      avatar_size,
      avatar_img_id,
      undefined,
      false,
      this.dom_mode ? avatar_size / 2 : 0
    );

    this.addChild( this.avatar_image, true );

    this.applyFacebookPhotoIfNeeded();
  }

  // For the Facebook avatar (id 987) the placeholder ImgID is already set; if we
  // have the real photo URL, fetch it and swap in the decoded bitmap.
  private applyFacebookPhotoIfNeeded()
  {
    if ( this.avatar_id !== AvatarUtils.FACEBOOK_AVATAR_ID || !this.facebook_av_url )
      return;

    const url = this.facebook_av_url;
    if ( this.dom_mode )
    {
      this.avatar_image.setImageUrl( url );
      return;
    }

    loadFacebookAvatar( url )
      .then( bitmap =>
      {
        // The avatar/url may have changed while the photo was loading.
        if ( this.avatar_id === AvatarUtils.FACEBOOK_AVATAR_ID && this.facebook_av_url === url )
        {
          this.avatar_image.setImageBitmap( bitmap );
          // Repaint the parent (scroll row / card) so the photo replaces the
          // placeholder even though scroll rows are rendered only once.
          this.forceRenderParent();
        }
      } )
      .catch( () => { /* keep placeholder on failure */ } );
  }

  public setSelected( selected: boolean )
  {
    this.is_selected = selected;
    this.setShouldBeRendered();
  }

  private dom_last_key = "";
  private dom_icons: HTMLDivElement | null = null;

  private domBorderBackground(): string
  {
    if ( typeof this.border_color === "string" )
      return this.border_color;
    const direction = this.border_color.direction === "horizontal" ? "to right" : "to bottom";
    const stops = this.border_color.stops
      ? this.border_color.stops.map( s => `${s.color} ${s.offset * 100}%` )
      : [ `${this.border_color.from ?? "#000000"} 0%`, `${this.border_color.to ?? "#000000"} 100%` ];
    return `linear-gradient(${direction}, ${stops.join( ", " )})`;
  }

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    const w = this.rect.getW();
    const ring = w + this.border_size_offset;
    const background = this.domBorderBackground();
    const icon_size = LayoutScaler.widthFromDesign( 50 );
    const sel_size = LayoutScaler.widthFromDesign( 90 );
    const sel_x = w - sel_size + LayoutScaler.widthFromDesign( 18 );
    const sel_y = this.rect.getH() - sel_size + LayoutScaler.heightFromDesign( 4 );
    const reg_url = this.registration_icon_id ? ImagesHolder.getUrl( this.registration_icon_id ) : null;
    const sel_url = this.is_selected ? ImagesHolder.getUrl( ImgID.IconAvatarSelected ) : null;
    const key = `${ring}|${background}|${reg_url ?? ""}|${sel_url ?? ""}|${icon_size}|${sel_size}`;
    if ( key === this.dom_last_key )
      return true;
    this.dom_last_key = key;

    const s = this.getDomSkin().style;
    s.left = `${-this.border_size_offset / 2}px`;
    s.top = `${-this.border_size_offset / 2}px`;
    s.width = `${ring}px`;
    s.height = `${ring}px`;
    s.borderRadius = "50%";
    s.background = background;

    if ( !this.dom_icons )
    {
      this.dom_icons = document.createElement( "div" );
      this.dom_icons.className = "ui-dom-avatar-icons";
      const is = this.dom_icons.style;
      is.position = "absolute";
      is.left = "0";
      is.top = "0";
      is.width = "100%";
      is.height = "100%";
      is.pointerEvents = "none";
    }
    if ( node.lastElementChild !== this.dom_icons )
      node.appendChild( this.dom_icons );

    let html = "";
    if ( reg_url )
      html += `<div style="position:absolute;left:${w - icon_size}px;top:${this.rect.getH() - icon_size}px;width:${icon_size}px;height:${icon_size}px;background:url('${reg_url}') center / 100% 100% no-repeat"></div>`;
    if ( sel_url )
      html += `<div style="position:absolute;left:${sel_x}px;top:${sel_y}px;width:${sel_size}px;height:${sel_size}px;background:url('${sel_url}') center / 100% 100% no-repeat"></div>`;
    this.dom_icons.innerHTML = html;
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    this.drawBorder();

    const avatar_size = this.rect.getW() - this.border_size_offset * 2;
    const radius = avatar_size / 2;

    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc( 0, 0, radius, 0, Math.PI * 2 );
    this.ctx.clip();

    this.renderChildren();

    this.ctx.restore();

    if ( this.registration_icon_id )
      this.drawRegistrationIcon();

    if ( this.is_selected )
      this.drawSelectedImage();

    this.finishRender();
  }

  private drawBorder()
  {
    const radius = this.rect.getW() / 2 + this.border_size_offset / 2;

    this.ctx.beginPath();
    this.ctx.arc( 0, 0, radius, 0, Math.PI * 2 );
    this.ctx.fillStyle = this.createBorderFillStyle( radius );
    this.ctx.fill();
  }

  private createBorderFillStyle( radius: number ): string | CanvasGradient
  {
    if ( typeof this.border_color === "string" )
      return this.border_color;

    const gradient = this.border_color.direction === "horizontal"
      ? this.ctx.createLinearGradient( -radius, 0, radius, 0 )
      : this.ctx.createLinearGradient( 0, -radius, 0, radius );

    if ( this.border_color.stops )
    {
      for ( const stop of this.border_color.stops )
        gradient.addColorStop( stop.offset, stop.color );
    }
    else
    {
      gradient.addColorStop( 0, this.border_color.from ?? "#000000" );
      gradient.addColorStop( 1, this.border_color.to ?? "#000000" );
    }

    return gradient;
  }

  private drawRegistrationIcon()
  {
    const design_icon_size = 50;
    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const x = this.rect.getW2() - icon_size;
    const y = this.rect.getH2() - icon_size;

    this.ctx.drawImage(
      ImagesHolder.getImage( this.registration_icon_id! ),
      x,
      y,
      icon_size,
      icon_size
    );
  }

  private drawSelectedImage()
  {
    const design_size = 90;
    const design_offset_x = 18;
    const design_offset_y = 4;
    const size = LayoutScaler.widthFromDesign( design_size );
    const offset_x = LayoutScaler.widthFromDesign( design_offset_x );
    const offset_y = LayoutScaler.heightFromDesign( design_offset_y );
    const x = this.rect.getW2() - size + offset_x;
    const y = this.rect.getH2() - size + offset_y;

    this.ctx.drawImage(
      ImagesHolder.getImage( ImgID.IconAvatarSelected ),
      x,
      y,
      size,
      size,
    );
  }

  public name(): string
  {
    return "UIAvatarSelectionItem";
  }
}
